import React, { useCallback, useEffect, useRef, useState } from 'react';
import './tap.css';

const IDLE_TIMEOUT_MS = 3500;
const MAX_INTERVALS = 12;

export default function TapBpm({ onBpmChange, resetSignal }) {
  const [taps, setTaps] = useState([]);
  const [bpm, setBpm] = useState(null);
  const [pulse, setPulse] = useState(0);
  const idleTimerRef = useRef(null);

  useEffect(() => {
    if (resetSignal === undefined) return;
    setTaps([]);
    setBpm(null);
  }, [resetSignal]);

  const clearIdleTimer = () => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  };

  const reset = useCallback(() => {
    clearIdleTimer();
    setTaps([]);
    setBpm(null);
  }, []);

  const handleTap = useCallback(() => {
    const now = performance.now();
    setPulse((p) => p + 1);

    setTaps((prev) => {
      // If user paused too long, start a new measurement
      const last = prev[prev.length - 1];
      const baseline = last && now - last > IDLE_TIMEOUT_MS ? [] : prev;
      const next = [...baseline, now].slice(-MAX_INTERVALS - 1);

      if (next.length >= 2) {
        const intervals = [];
        for (let i = 1; i < next.length; i++) {
          intervals.push(next[i] - next[i - 1]);
        }
        const avg = intervals.reduce((a, b) => a + b, 0) / intervals.length;
        const computed = Math.round(60000 / avg);
        if (computed > 20 && computed < 240) {
          setBpm(computed);
          if (onBpmChange) onBpmChange(computed);
        }
      }
      return next;
    });

    clearIdleTimer();
    idleTimerRef.current = setTimeout(() => {
      // do nothing — the last computed bpm stays
    }, IDLE_TIMEOUT_MS);
  }, [onBpmChange]);

  // Allow spacebar to tap when the pad is focused
  const handleKeyDown = (e) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      handleTap();
    }
  };

  useEffect(() => () => clearIdleTimer(), []);

  const tapCount = taps.length;
  const quality =
    tapCount < 4 ? 'low' : tapCount < 8 ? 'med' : 'high';

  return (
    <section className="tap-section">
      <div className="tap-header">
        <div>
          <h2 className="tap-title">Tap your pulse</h2>
          <p className="tap-sub">
            Find your pulse, then tap the pad in time with each beat.
          </p>
        </div>
        <button
          type="button"
          className="tap-reset"
          onClick={reset}
          disabled={tapCount === 0 && bpm === null}
        >
          Reset
        </button>
      </div>

      <button
        type="button"
        className={`tap-pad pulse-${pulse % 2}`}
        onClick={handleTap}
        onKeyDown={handleKeyDown}
        aria-label="Tap to measure heart rate"
      >
        <div className="tap-readout">
          <span className="tap-bpm">{bpm ?? '—'}</span>
          <span className="tap-bpm-label">BPM</span>
        </div>
        <div className="tap-meta">
          <span>{tapCount} {tapCount === 1 ? 'tap' : 'taps'}</span>
          <span className={`tap-quality q-${quality}`}>
            {tapCount === 0 ? 'ready' : quality === 'low' ? 'keep tapping' : quality === 'med' ? 'good' : 'great'}
          </span>
        </div>
      </button>
      <p className="tap-hint">Tip: press space or enter while focused on the pad.</p>
    </section>
  );
}
